import React, {useContext, useEffect, useLayoutEffect, useState} from 'react';
import {Modal, Platform, ScrollView, View} from 'react-native';
import Button from '../../../../../components/Button';
import DateTimePicker from '../../../../../components/DateTimePicker/DateTimePicker';
import Div from '../../../../../lib/Div';
import {useFormik} from 'formik';
import Input from '../../../../../components/Input';
import MainStyle from '../../../../../../MainStyle';
import {colors} from '../../../../../assets/styles/colors';
import * as Yup from "yup";
import {StoreContext} from "../../../../../components/StoreContextProvider";
import {updateUserPayloadType} from "../../../../../types";
import ServiceClient from "../../../../../utils/ServiceClient";
import P from "../../../../../lib/P";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {dateFormat, isFullSwing, LANDING_PAGE_URL} from "../../../../../constants";
import moment, {Moment} from "moment";
import Loader from "../../../../../components/Loader";
import FSHeader from "../../../../../components/FSHeader";
import {TextInput} from 'react-native-paper';
import Btn from "../../../../../components/Btn";
import {getFieldError} from "../../../../../utils";
import {LATIN_KEYBOARD_MESSAGE, LATIN_KEYBOARD_REGEX} from "../../../../../utils/inputValidation";

const initialValues: { firstname: string, lastname: string, dateOfBirth: Moment | null } = {
  firstname: '', lastname: '', dateOfBirth: null
};

type Props = { isOpen: boolean, onClose: () => void, onSubmit: () => void };

const VerifyInformationModal = (props: Props) => {
  const {isOpen} = props;
  const Context = useContext(StoreContext);
  const {cancelModalConfig, user} = Context;
  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      firstname: Yup.string().required().matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE),
      lastname: Yup.string().required().matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE),
      dateOfBirth: Yup.mixed<Moment>().nullable().required()
    }),
    onSubmit: () => {
      updateUser();
    }
  });
  const [loading, setLoading] = useState(false);

  const updateUser = () => {
    if (!user) return;
    setLoading(true);

    const {firstname, lastname, dateOfBirth} = formik.values;
    const {
      allowTextNotifications, allowEmailNotifications, allowPartnerPromos, handicap, username, handedness, gender
    } = user;

    const payload: updateUserPayloadType = {
      username, handedness, allowTextNotifications, allowEmailNotifications, allowPartnerPromos, handicap, firstname,
      lastname, gender
    };

    if (dateOfBirth) payload.dateOfBirth = dateOfBirth.format();

    ServiceClient.updateUser(payload).then(() => {
      setLoading(false);
      Context.getUser().then();
      props.onClose();
      props.onSubmit();

    }).catch(err => {
      console.log('UpdateUser error', err);
      setLoading(false);

      Context.setCancelModalConfig({
        visible: true, subtitle: err.message || 'UpdateUser Error', title: 'Error', leftBtnText: 'OK',
        onExit: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  useEffect(() => {
    if (isOpen) {
      formik.resetForm({
        values: {
          firstname: user?.firstname || '', lastname: user?.lastname || '',
          dateOfBirth: user?.dateOfBirth ? moment(user.dateOfBirth, dateFormat) : null
        }
      });
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!!user?.userId) return;

    (async () => {
      const val = await AsyncStorage.getItem('session');

      if (val) {
        const Session = JSON.parse(val);
        Context.setSession(Session);

        try {
          await Context.getUser(Session);

        } catch (e: any) {
          console.log('get curUser err', e);
          Context.setCancelModalConfig({
            visible: true, title: 'Error', subtitle: e?.message || 'Error', rightBtnText: 'OK',
            onResume: () => {
              Context.setCancelModalConfig({...cancelModalConfig, visible: false});
              Context.logout().then(() => {
                if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
                Context.setLoadingOverlay(false);
              });
            }
          });
        }
      }
    })();
  }, [user?.userId]);

  return (
    <Modal animationType="fade" transparent visible={isOpen} statusBarTranslucent={false} hardwareAccelerated={false}
           onRequestClose={props.onClose}>
      <View style={[MainStyle.fix_gradient_and_modal_error_in_landscape, MainStyle.flex1, MainStyle.bg_filter_modal]}>
        {isFullSwing ? (
          <>
            <FSHeader
              title="Verify Your Identity" px={20} hideLogo onCancel={props.onClose} hideAccount hideMenu hideGameLogo
              hideBalance
            />

            <Div pt={39} px={15} bg="#131314" flex={1}>
              <P fontFamily="DavisSans-Regular" ta="center" mb={36}>
                Please verify that this information matches your identification card or passport before proceeding.
              </P>

              <TextInput
                label="First Name" onBlur={formik.handleBlur('firstname')} style={[MainStyle.b_r_4, MainStyle.m_b_20]}
                value={formik.values.firstname} error={!!getFieldError('firstname', formik)}
                onChangeText={text => formik.setFieldValue('firstname', text)} underlineColor="transparent"
              />

              <TextInput
                label="Last Name" onBlur={formik.handleBlur('lastname')} style={[MainStyle.b_r_4, MainStyle.m_b_20]}
                value={formik.values.lastname} error={!!getFieldError('lastname', formik)}
                onChangeText={text => formik.setFieldValue('lastname', text)} underlineColor="transparent"
              />

              <DateTimePicker
                formik={formik} h="56px" bg="#252526" rounded="4px" placeholder="Date of Birth" fs="16px"
                textAlign="left" variant="filled"
              />

              <Div flex={1}/>

              <Btn mb={34} bg="#3369FF" rounded={4} onPress={formik.submitForm}>
                <P lh={56} ta="center" fontFamily="DavisSans-Medium">Next</P>
              </Btn>
            </Div>
          </>
        ) : (
          <ScrollView contentContainerStyle={[MainStyle.flex1, MainStyle.p_x_20]}>
            <Div minH="100%" alignItems="center" justifyContent="center">
              <Div maxW={390} w="100%" rounded={20} borderWidth={1} borderColor={colors.settings_border}
                   bg={colors.settings_bg} pt={30} alignItems="center">
                <P ta="center" fs={26} lh={26} mb={24} fw="500">Verify Information</P>

                <P fs={18} ta="center" fw="500" mb={16}>
                  {`Please verify this information matches\nyour identification card or passport\nbefore proceeding.`}
                </P>

                <Input
                  placeholder="Enter Text Here" label="FIRST NAME" value={formik.values.firstname} bg="transparent"
                  onChangeText={formik.handleChange('firstname')} onBlur={formik.handleBlur('firstname')}
                  error={getFieldError('firstname', formik)} onFocusScroll mb={9} w={240} h={48} textAlign="center"
                />

                <Input
                  placeholder="Enter Text Here" label="LAST NAME" value={formik.values.lastname} bg="transparent"
                  onChangeText={formik.handleChange('lastname')} onBlur={formik.handleBlur('lastname')}
                  error={getFieldError('lastname', formik)} onFocusScroll mb={9} w={240} h={48} textAlign="center"
                />

                <DateTimePicker
                  formik={formik as any} mb="21px" label="DATE OF BIRTH" h="48px" w="240px" bg="transparent" variant="outlined"
                />

                <Button onPress={formik.submitForm} text="Submit" w={309} fontWeight="600" mb={10}/>

                <Button
                  text="Cancel" w={309} bg={colors.loading_login} borderWidth={1} borderColor="white"
                  onPress={props.onClose} color="white" mb={33}
                />
              </Div>
            </Div>
          </ScrollView>
        )}
      </View>

      <Loader visible={loading}/>
    </Modal>
  );
};

export default React.memo(VerifyInformationModal);
